import React, { useEffect, useState, useCallback, useRef } from 'react'
import { IndexInfoDepEdu, GetStudentsBySchoolsPagination } from '../../Api'
import { useNavigate } from 'react-router-dom'

import {
    Container,
    List,
    Emp,
    Span,
    Search,
    FormFilter,
    FormSearch,
    AreaEmp,
    InputEmp,
    Select,
    ContainerDivs
} from './style'

import LoadingSpinner from '../../components/Loading'

const LIMIT = 20

const StudentsMunicipality = () => {

    const navigate = useNavigate()

    const [students, setStudents] = useState([])
    const [schools, setSchools] = useState([])
    const [idsSchools, setIdsSchools] = useState([])
    const [counts, setCounts] = useState({ ativo: 0, transferido: 0, inativo: 0, concluido: 0 })
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(false)

    const [busca, setBusca] = useState("")
    const [debouncedBusca, setDebouncedBusca] = useState("")
    const [filterSchool, setFilterSchool] = useState("")

    const [loading, setLoading] = useState(true)        // spinner cheio: SÓ 1ª carga
    const [listLoading, setListLoading] = useState(false) // refetch (filtro/busca)
    const [loadingMore, setLoadingMore] = useState(false) // scroll infinito

    const initializedRef = useRef(false)
    const fetchingRef = useRef(false)
    const sentinelRef = useRef(null)

    // 🔹 Debounce da busca
    useEffect(() => {
        const t = setTimeout(() => setDebouncedBusca(busca), 400)
        return () => clearTimeout(t)
    }, [busca])

    // 🔹 Busca paginada
    const fetchStudents = useCallback(async (pageToLoad, reset, ids) => {
        if (fetchingRef.current) return
        fetchingRef.current = true
        try {
            const res = await GetStudentsBySchoolsPagination({
                idsSchools: ids,
                page: pageToLoad,
                limit: LIMIT,
                schoolId: filterSchool,
                search: debouncedBusca
            })
            console.log("alunos", res)
            const { data, hasMore: more, total: t, counts: c } = res.data

            setTotal(t ?? 0)
            setHasMore(Boolean(more))
            if (c) setCounts(c)
            setStudents(prev => (reset ? data : [...prev, ...data]))
        } finally {
            fetchingRef.current = false
        }
    }, [filterSchool, debouncedBusca])

    // 🔹 Setup inicial: carrega só a lista de escolas (leve) e a 1ª página
    useEffect(() => {
        (async () => {
            setLoading(true)

            const idEducationDepartment = sessionStorage.getItem("idDepartment")
            const response = await IndexInfoDepEdu(idEducationDepartment)
            const schoolsData = response.data.data.id_schools

            setSchools(schoolsData)

            const ids = schoolsData.map(s => s._id)
            setIdsSchools(ids)

            await fetchStudents(1, true, ids)

            setLoading(false)
            initializedRef.current = true
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // 🔹 Recarrega da página 1 quando escola/busca mudam
    //    (input NÃO desmonta — só aviso discreto na lista)
    useEffect(() => {
        if (!initializedRef.current) return
        (async () => {
            setListLoading(true)
            setPage(1)
            await fetchStudents(1, true, idsSchools)
            setListLoading(false)
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterSchool, debouncedBusca])

    const loadMore = useCallback(async () => {
        if (fetchingRef.current || !hasMore) return
        const next = page + 1
        setLoadingMore(true)
        setPage(next)
        await fetchStudents(next, false, idsSchools)
        setLoadingMore(false)
    }, [page, hasMore, fetchStudents, idsSchools])

    // 🔹 Scroll infinito
    useEffect(() => {
        const node = sentinelRef.current
        if (!node || !hasMore || loading) return

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) loadMore()
            },
            { rootMargin: '300px' }
        )

        observer.observe(node)
        return () => observer.disconnect()
    }, [hasMore, loading, loadMore])

    const StudentInformation = (student) => {
        sessionStorage.setItem("id-school", JSON.stringify(student.id_school._id))

        if (student.id_school?.assessmentFormat) {
            sessionStorage.setItem("assessmentFormat", student.id_school.assessmentFormat)
        }

        navigate(`/student/info/${student._id}`)
    }

    return (
        <Container>
            {loading ? (
                <LoadingSpinner />
            ) : (
                <ContainerDivs>

                    {/* 🔍 BUSCA + FILTRO */}
                    <Search>
                        <FormSearch>
                            <label>Buscar Aluno</label>
                            <AreaEmp>
                                <InputEmp
                                    type="text"
                                    placeholder="Buscar por nome"
                                    value={busca}
                                    onChange={(e) => setBusca(e.target.value)}
                                />
                            </AreaEmp>
                        </FormSearch>

                        <FormFilter>
                            <label>Filtrar por Escola</label>
                            <Select
                                value={filterSchool}
                                onChange={(e) => setFilterSchool(e.target.value)}
                            >
                                <option value="">Todas</option>
                                {schools.map(s => (
                                    <option key={s._id} value={s._id}>
                                        {s.name}
                                    </option>
                                ))}
                            </Select>
                        </FormFilter>
                    </Search>

                    {/* 📋 LISTA */}
                    <List>
                        <p>
                            Total de Alunos: {total}
                            {listLoading && " — atualizando..."}
                        </p>
                        <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
                            <p style={{ color: "var(--green)" }}>Ativos: {counts.ativo}</p>
                            <p style={{ color: "#e08a0b" }}>Transferidos: {counts.transferido}</p>
                            <p style={{ color: "var(--coral, #e5484d)" }}>Inativos: {counts.inativo}</p>
                            <p style={{ color: "#3b82f6" }}>Concluídos: {counts.concluido}</p>
                        </div>

                        {students.map(student => (
                            <Emp
                                key={student._id}
                                onClick={() => StudentInformation(student)}
                            >
                                <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                                    <Span style={{ color: "var(--teal-text, var(--teal))" }}>
                                        {student.name}
                                    </Span>

                                    {student.id_school?.name && (
                                        <span style={{ color: "var(--text, var(--text-muted))", fontSize: "13px" }}>
                                            {student.id_school.name}
                                        </span>
                                    )}
                                </div>

                                {/* 🎯 STATUS */}
                                {student.status === "ativo" && (
                                    <span style={{ color: "var(--green)", marginLeft: 8, fontWeight: "bold" }}>
                                        ativo
                                    </span>
                                )}

                                {student.status === "transferido" && (
                                    <span style={{ color: "#e08a0b", marginLeft: 8, fontWeight: "bold" }}>
                                        transferido
                                    </span>
                                )}

                                {student.status === "inativo" && (
                                    <span style={{ color: "var(--coral, #e5484d)", marginLeft: 8, fontWeight: "bold" }}>
                                        inativo
                                    </span>
                                )}
                                {student.status === "concluido" && (
                                    <span style={{ color: "#3b82f6", marginLeft: "8px", fontWeight: "bold" }}>
                                        {student.status}
                                    </span>
                                )}
                            </Emp>
                        ))}

                        {/* sentinela do scroll infinito */}
                        <div ref={sentinelRef} style={{ height: 1 }} />

                        {loadingMore && (
                            <p style={{ textAlign: "center", padding: 8 }}>
                                Carregando mais alunos...
                            </p>
                        )}
                    </List>

                </ContainerDivs>
            )}
        </Container>
    )
}

export default StudentsMunicipality
